import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './KpiReportPage.css';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function KpiReportPage() {
  const [start,   setStart]   = useState(`${new Date().getFullYear()}-01-01`);
  const [end,     setEnd]     = useState(todayISO());
  const [label,   setLabel]   = useState('');

  const [jobId,     setJobId]     = useState(null);
  const [jobLogs,   setJobLogs]   = useState([]);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobResult, setJobResult] = useState(null);
  const [logsOpen,  setLogsOpen]  = useState(true);
  const [reports,   setReports]   = useState([]);
  const [driveLink, setDriveLink] = useState(null);
  const [error,     setError]     = useState(null);
  const [loading,   setLoading]   = useState(false);
  const logViewerRef = useRef();

  function loadReports() {
    axios.get('/api/kpi-report/reports')
      .then(r => setReports(r.data.slice().reverse()))
      .catch(() => {});
  }

  useEffect(() => {
    loadReports();
    axios.get('/api/kpi-report/drive-folder')
      .then(r => setDriveLink(r.data.link))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!jobId || jobStatus === 'complete' || jobStatus === 'error') return;
    const iv = setInterval(() => {
      axios.get(`/api/kpi-report/logs/${jobId}`)
        .then(r => {
          setJobLogs(r.data.lines);
          setJobStatus(r.data.status);
          if (r.data.status === 'complete' && r.data.result) {
            setJobResult(r.data.result);
            loadReports();
          } else if (r.data.status === 'error') {
            setError(r.data.error || 'Job failed');
          }
        })
        .catch(() => {});
    }, 2000);
    return () => clearInterval(iv);
  }, [jobId, jobStatus]);

  useEffect(() => {
    if (!logsOpen || !logViewerRef.current) return;
    const el = logViewerRef.current;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [jobLogs, logsOpen]);

  function handleRun() {
    if (!start || !end || !label.trim()) {
      setError('Start date, end date, and label are all required');
      return;
    }
    setLoading(true);
    setError(null);
    setJobResult(null);
    axios.post('/api/kpi-report/run', { start, end, label: label.trim() })
      .then(r => {
        setJobId(r.data.job_id);
        setJobLogs([]);
        setJobStatus('pending');
        setLogsOpen(true);
        setLoading(false);
      })
      .catch(e => {
        setError(e.response?.data?.error || e.message);
        setLoading(false);
      });
  }

  const busy = jobStatus === 'pending' || jobStatus === 'running';

  return (
    <div className="kr-page">
      <div className="kr-header-row">
        <div>
          <h1>Distributor KPI Report</h1>
          <p className="kr-muted">
            Pulls live Shopify order data for a date range and builds an Excel workbook with
            sales-channel, product, sleeve, deckbox, color, geography, and AOV breakdowns —
            for distributor/partner meetings. Every report is saved to the shared Drive folder
            below (shared with Randy and Geoff).
          </p>
        </div>
        {driveLink && (
          <a className="kr-drive-folder-link" href={driveLink} target="_blank" rel="noreferrer">
            📁 Open Drive folder ↗
          </a>
        )}
      </div>

      {error && <div className="kr-error">{error}</div>}

      <div className="kr-form">
        <label className="kr-field">
          <span>Start date</span>
          <input type="date" value={start} onChange={e => setStart(e.target.value)} />
        </label>
        <label className="kr-field">
          <span>End date</span>
          <input type="date" value={end} onChange={e => setEnd(e.target.value)} />
        </label>
        <label className="kr-field kr-field-label">
          <span>Label</span>
          <input type="text" placeholder='e.g. "2026 1H" or "2025"'
            value={label} onChange={e => setLabel(e.target.value)} />
        </label>
        <button className="kr-btn-primary" onClick={handleRun} disabled={loading || busy}>
          {loading ? 'Starting…' : busy ? '● Running…' : '▶  Generate Report'}
        </button>
      </div>

      {jobId && (
        <div className="kr-log-section">
          <div className="kr-log-header">
            <div className="kr-log-title-row">
              <span className="kr-log-title">Report Job</span>
              {jobStatus && (
                <span className={`kr-badge kr-badge-${jobStatus}`}>
                  {jobStatus === 'pending'  ? 'Queued'      :
                   jobStatus === 'running'  ? '● Running'   :
                   jobStatus === 'complete' ? '✓ Complete'  :
                   jobStatus === 'error'    ? '✕ Error'     : jobStatus}
                </span>
              )}
            </div>
            <div className="kr-log-controls">
              <button className="kr-btn-secondary sm" onClick={() => setLogsOpen(o => !o)}>
                {logsOpen ? 'Collapse' : 'Expand'}
              </button>
              {(jobStatus === 'complete' || jobStatus === 'error') && (
                <button className="kr-btn-secondary sm"
                  onClick={() => { setJobId(null); setJobLogs([]); setJobStatus(null); }}>
                  Dismiss
                </button>
              )}
            </div>
          </div>
          {logsOpen && (
            <div className="kr-log-viewer" ref={logViewerRef}>
              {jobLogs.length === 0
                ? <span className="kr-log-muted">Waiting for job to start…</span>
                : jobLogs.map((line, i) => <div key={i} className="kr-log-line">{line}</div>)
              }
            </div>
          )}
          {jobResult && (
            <div className="kr-log-result">
              Report ready —{' '}
              <a href={`/api/kpi-report/download/${jobResult.filename}`}>
                ⬇ {jobResult.filename}
              </a>
              {jobResult.drive_link && (
                <>
                  {' · '}
                  <a href={jobResult.drive_link} target="_blank" rel="noreferrer">
                    open in Drive ↗
                  </a>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {reports.length > 0 && (
        <section className="kr-saved-section">
          <h2 className="kr-section-title">Previous Reports</h2>
          <div className="kr-saved-grid">
            {reports.map(r => (
              <div key={r.name} className="kr-saved-card">
                <a className="kr-saved-name" href={`/api/kpi-report/download/${r.name}`}>
                  {r.name}
                </a>
                <span className="kr-saved-ts">
                  {new Date(r.modified * 1000).toLocaleString()} · {r.size_kb} KB
                </span>
                {r.drive_link && (
                  <a className="kr-saved-drive" href={r.drive_link} target="_blank" rel="noreferrer">
                    open in Drive ↗
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
